import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid";
import { FC, useMemo, useState } from "react";

import { Combobox } from "@headlessui/react";
import _ from "lodash";
import { classNames } from "utils";
import { useEmployees } from "hooks/data/use-employees";

interface EmployeePickerProps {
  id: string;
  label?: string;
  placeholder?: string;
  accessor?: string;
  required?: boolean;
  disabled?: boolean;
  labelHidden?: boolean;
  values: any;
  errors?: any;
  touched?: any;
  optionsLoading?: boolean;
  setFieldValue: any;
  setFieldTouched: any;
  setFieldError: any;
  rounded?: boolean;
  rawId?: boolean;
  multiple?: boolean;
}

const EmployeePicker: FC<EmployeePickerProps> = ({
  id,
  values,
  setFieldValue,
  setFieldTouched,
  placeholder,
  label,
  errors,
  touched,
  required,
  labelHidden,
  disabled,
  rounded,
  rawId,
  accessor,
  multiple = false,
}) => {
  const [query, setQuery] = useState("");
  const { employees, loading } = useEmployees({
    page: 1,
    pageSize: 500,
    offset: 0,
    limit: 500,
    search: "",
    sort: "",
  });

  const options = useMemo(
    () =>
      employees?.map(({ employee_first_name, ...rest }) => ({
        label: `${employee_first_name} ${_.get(
          rest,
          "employee_last_name",
          ""
        )}`,
        value: rawId
          ? _.get(rest, accessor || "employee_id", "")
          : { employee_first_name, ...rest },
      })),
    [accessor, employees, rawId]
  );

  const filteredOptions = useMemo(
    () =>
      query === ""
        ? options
        : options.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        ),
    [options, query]
  );

  return (
    <Combobox
      as="div"
      disabled={disabled}
      multiple={multiple as any}
      value={_.get(values, id, multiple ? [] : null)}
      onChange={(val) => {
        setFieldTouched(id, true);
        if (multiple) {
          const currentValues = _.get(values, id, []);
          const isSelected = currentValues?.some((v: any) => _.isEqual(v, val));
          if (isSelected) {
            setFieldValue(
              id,
              currentValues.filter((v: any) => !_.isEqual(v, val))
            );
          } else {
            setFieldValue(id, [...currentValues, val]);
          }
        } else {
          setFieldValue(id, val);
        }
      }}
    // {...{ multiple: multiple ? true : false }}
    >
      {!labelHidden && (
        <Combobox.Label className="block text-sm font-medium text-gray-700">
          {label} {required ? <span className="text-red-500">*</span> : ""}
        </Combobox.Label>
      )}
      <div className={classNames(labelHidden ? "" : "mt-1", "relative")}>
        <Combobox.Input
          className={classNames(
            disabled ? "cursor-not-allowed bg-gray-100" : "bg-white",
            "w-full border border-gray-300 py-2 pl-3 pr-10 shadow-sm focus:border-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm placeholder:font-light placeholder:text-xs",
            !rounded ? "rounded-md" : "rounded-none"
          )}
          placeholder={loading ? "please wait ..." : placeholder}
          autoComplete="none"
          onChange={(event) => setQuery(event.target.value)}
          displayValue={(selectedOptions: any) => {
            if (multiple) {
              if (!selectedOptions || selectedOptions.length === 0) return "";
              return selectedOptions
                .map((selected: any) => options.find((o) => _.isEqual(o.value, selected))?.label)
                .join(", ");
            } else {
              return options.find((o) => _.isEqual(o.value, selectedOptions))?.label ?? "";
            }
          }}
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </Combobox.Button>
        {filteredOptions.length > 0 || loading ? (
          <Combobox.Options className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOptions.length === 0 && query !== "" ? (
              <div className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-700">
                No employees found.
              </div>
            ) : (
              <>
                {!multiple && (
                  <Combobox.Option
                    value={null}
                    className={({ active }) =>
                      classNames(
                        "relative cursor-default select-none py-2 pl-3 pr-9 font-light",
                        active ? "bg-primary-600 text-white" : "text-gray-500"
                      )
                    }
                  >
                    {({ active, selected }) => (
                      <>
                        <div className="flex items-center">
                          <span>Select One</span>
                        </div>
                        {selected && (
                          <span
                            className={classNames(
                              "absolute inset-y-0 right-0 flex items-center pr-4",
                              active ? "text-white" : "text-primary-600"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                )}
                {filteredOptions.map((option) => (
                  <Combobox.Option
                    key={JSON.stringify(option.value)}
                    value={option.value}
                    className={({ active }) =>
                      classNames(
                        "relative cursor-default select-none py-2 pl-3 pr-9",
                        active ? "bg-primary-600 text-white" : "text-gray-900"
                      )
                    }
                  >
                    {({ active, selected }) => {
                      const isOptionSelected = multiple
                        ? _.get(values, id, []).some((v: any) => _.isEqual(v, option.value))
                        : selected;
                      return (
                        <>
                          <div className="flex items-center">
                            <span
                              className={classNames(
                                "truncate",
                                isOptionSelected ? "font-semibold" : ""
                              )}
                            >
                              {option.label}
                            </span>
                          </div>
                          {isOptionSelected && (
                            <span
                              className={classNames(
                                "absolute inset-y-0 right-0 flex items-center pr-4",
                                active ? "text-white" : "text-primary-600"
                              )}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      );
                    }}
                  </Combobox.Option>
                ))}
              </>
            )}
          </Combobox.Options>
        ) : null}
      </div>
      {_.get(errors, id) && _.get(touched, id) ? (
        <p className="mt-2 text-sm text-red-600" id={`${id}-error`}>
          {_.get(errors, id)}
        </p>
      ) : null}
    </Combobox>
  );
};

export default EmployeePicker;